import { useState } from 'react';
import { ActionMenu, ActionList, TextInput } from '@primer/react';
import { PlusIcon, SearchIcon } from '@primer/octicons-react';
import styled from 'styled-components';
import { AddButton, ListToken } from './shared/tokens';
import { SectionContainer, ItemsContainer, ItemsList } from './shared/section-styles';
import { SectionTitleWithGuidance, SectionWarning } from './shared/section-guidance';
import { Recyclability } from '../../../../../../common/model/input/companies/products/ipr';
import { ATTRIBUTE_GUIDANCE } from '@upright/common/model/input/companies/products/ipr/attribute-guidance';

interface RecyclabilityProps {
  recyclability: RecyclabilityItem[];
  onRecyclabilityChange: (recyclability: RecyclabilityItem[]) => void;
  warning?: string;
}

export interface RecyclabilityItem {
  id: Recyclability;
  title: string;
}

const RECYCLABILITY_OPTIONS: RecyclabilityItem[] = Object.values(Recyclability).map(value => ({
  id: value,
  title: value,
}));

export function RecyclabilityAttributes({ recyclability, onRecyclabilityChange, warning }: RecyclabilityProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const availableRecyclability = RECYCLABILITY_OPTIONS.filter(
    recyclabilityOption =>
      !recyclability.some(r => r.id === recyclabilityOption.id) &&
      recyclabilityOption.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const addRecyclability = (recyclabilityOption: RecyclabilityItem) => {
    onRecyclabilityChange([...recyclability, recyclabilityOption]);
    setIsMenuOpen(false);
    setSearchTerm('');
  };

  const handleMenuOpenChange = (open: boolean) => {
    setIsMenuOpen(open);
    if (!open) {
      setSearchTerm('');
    }
  };

  const removeRecyclability = (recyclabilityOption: RecyclabilityItem) => {
    onRecyclabilityChange(recyclability.filter(r => r.id !== recyclabilityOption.id));
  };

  return (
    <SectionContainer>
      <SectionTitleWithGuidance title="Recyclability" guidance={ATTRIBUTE_GUIDANCE.recyclability} />
      <ItemsContainer>
        <ItemsList>
          {recyclability.map(recyclabilityOption => (
            <ListToken
              key={recyclabilityOption.id}
              size="medium"
              onRemove={() => {
                removeRecyclability(recyclabilityOption);
              }}
              text={recyclabilityOption.title}
            />
          ))}
          <ActionMenu open={isMenuOpen} onOpenChange={handleMenuOpenChange}>
            <ActionMenu.Anchor>
              <AddButton size="medium" leadingVisual={PlusIcon} text="Add recyclability" />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay width="medium">
              <SearchContainer>
                <TextInput
                  placeholder="Search recyclability options..."
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                  }}
                  leadingVisual={SearchIcon}
                  size="small"
                />
              </SearchContainer>
              <ScrollableActionListContainer>
                <ActionList>
                  <ActionList.Group title="Select a recyclability option...">
                    {availableRecyclability.length > 0 ? (
                      availableRecyclability.map(recyclabilityOption => (
                        <ActionList.Item
                          key={recyclabilityOption.id}
                          onSelect={() => {
                            addRecyclability(recyclabilityOption);
                          }}
                        >
                          {recyclabilityOption.title}
                        </ActionList.Item>
                      ))
                    ) : (
                      <ActionList.Item disabled>
                        {searchTerm ? 'No matching options found' : 'All recyclability options added'}
                      </ActionList.Item>
                    )}
                  </ActionList.Group>
                </ActionList>
              </ScrollableActionListContainer>
            </ActionMenu.Overlay>
          </ActionMenu>
        </ItemsList>
      </ItemsContainer>
      <SectionWarning warning={warning} />
    </SectionContainer>
  );
}

const SearchContainer = styled.div`
  padding: var(--space__sm);
  border-bottom: 1px solid var(--borderColor-default);
`;

const ScrollableActionListContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;
